import type { Question, SurveyPage } from '../components/QuestionBuilder'
import { normalizeDisplayText } from './surveyPresentation'

type Answers = Record<string, any>
const OTHER_OPTION_ID = '__other__'

export interface SurveyLocationQuestion {
  kind: 'question'
  index: number
}

export interface SurveyLocationTerminal {
  kind: 'terminal'
  page: SurveyPage
}

export type SurveyLocation = SurveyLocationQuestion | SurveyLocationTerminal

const buildOutcomeTerminal = (outcome: 'completed' | 'cancel_filter'): SurveyPage => ({
  id: `__terminal_${outcome}`,
  title: outcome === 'cancel_filter' ? 'Entrevista cancelada por filtro' : 'Pesquisa concluída',
  description: outcome === 'cancel_filter' ? 'Agradecemos sua participação.' : 'Agradecemos por responder à pesquisa.',
  questionIds: [],
  terminalOutcome: outcome,
})

const stripHtml = (value: string) => {
  if (typeof document === 'undefined') {
    return value.replace(/<[^>]+>/g, ' ')
  }

  const container = document.createElement('div')
  container.innerHTML = value
  return container.textContent || container.innerText || ''
}

const normalizePipedAnswerText = (value: unknown) => {
  const raw = stripHtml(String(value ?? ''))
  return normalizeDisplayText(raw)
    .replace(/\uFFFD/g, '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const normalizeComparableText = (value: string) =>
  stripHtml(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const normalizeToArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item))
  }

  if (value && typeof value === 'object' && Array.isArray(value.values)) {
    return value.values.map((item: any) => String(item))
  }

  if (value && typeof value === 'object' && value.value !== undefined) {
    return [String(value.value)]
  }

  if (value === undefined || value === null || value === '') {
    return []
  }

  return [String(value)]
}

const getOtherAnswerText = (value: any) => (
  value && typeof value === 'object' && typeof value.otherText === 'string'
    ? value.otherText.trim()
    : ''
)

const optionMatchesStoredValue = (
  option: Question['options'][number],
  storedValue: string
) => (
  String(option.id) === storedValue ||
  (
    option.value !== undefined &&
    option.value !== null &&
    String(option.value) === storedValue
  )
)

const matrixColumnMatchesStoredValue = (
  column: NonNullable<Question['matrixColumns']>[number],
  storedValue: string
) => (
  String(column.id) === storedValue ||
  (
    column.value !== undefined &&
    column.value !== null &&
    String(column.value) === storedValue
  )
)

const isVisibleChoiceValue = (
  value: string,
  visibleOptions: Question['options'],
  allowOther?: boolean
) => {
  if (value === OTHER_OPTION_ID) return !!allowOther
  return (visibleOptions || []).some((option) => optionMatchesStoredValue(option, value))
}

const getAnswerByPath = (answer: any, answerPath?: string) => {
  if (!answerPath) return answer
  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return undefined
  return answer[answerPath]
}

const findOptionByStoredValue = (
  options: Question['options'] = [],
  storedValue: string
) => options.find((option) => optionMatchesStoredValue(option, storedValue))

const buildTransferComparableValues = (
  selectedAnswers: string[],
  sourceQuestion?: Question,
  sourceAnswer?: any
) => {
  const comparableValues = new Set<string>()

  selectedAnswers.forEach((selectedAnswer) => {
    const normalizedAnswer = String(selectedAnswer)
    comparableValues.add(`id:${normalizedAnswer}`)

    if (normalizedAnswer === OTHER_OPTION_ID) {
      const otherText = getOtherAnswerText(sourceAnswer)
      if (otherText) {
        comparableValues.add(`text:${normalizeComparableText(otherText)}`)
        comparableValues.add(`value:${otherText}`)
      }
      return
    }

    comparableValues.add(`text:${normalizeComparableText(normalizedAnswer)}`)
    if (normalizedAnswer !== '') {
      comparableValues.add(`value:${normalizedAnswer}`)
    }

    const sourceOption = findOptionByStoredValue(sourceQuestion?.options, normalizedAnswer)
    if (sourceOption) {
      comparableValues.add(`id:${String(sourceOption.id)}`)
      comparableValues.add(`text:${normalizeComparableText(sourceOption.text || '')}`)
      if (sourceOption.value !== undefined && sourceOption.value !== null && String(sourceOption.value) !== '') {
        comparableValues.add(`value:${String(sourceOption.value)}`)
      }
    }
  })

  return comparableValues
}

const optionMatchesComparableValues = (
  option: Question['options'][number],
  comparableValues: Set<string>
) => {
  const optionId = `id:${String(option.id)}`
  const optionText = `text:${normalizeComparableText(option.text || '')}`
  const hasComparableValue = option.value !== undefined && option.value !== null && String(option.value) !== ''
  const optionValue = hasComparableValue ? `value:${String(option.value)}` : null

  return comparableValues.has(optionId) ||
    (optionValue !== null && comparableValues.has(optionValue)) ||
    comparableValues.has(optionText)
}

const buildTransferredOptionsFromAnswers = (
  selectedAnswers: string[],
  sourceQuestion?: Question,
  sourceAnswer?: any
): Question['options'] => {
  const transferredOptions: NonNullable<Question['options']> = []
  const usedIds = new Set<string>()

  selectedAnswers.forEach((selectedAnswer) => {
    const normalizedAnswer = String(selectedAnswer)
    const sourceOption = findOptionByStoredValue(sourceQuestion?.options, normalizedAnswer)
    const otherText = normalizedAnswer === OTHER_OPTION_ID ? getOtherAnswerText(sourceAnswer) : ''
    const fallbackLabel = otherText || (sourceOption
      ? stripHtml(sourceOption.text || '')
      : stripHtml(normalizedAnswer))
    const optionId = fallbackLabel || normalizedAnswer

    if (!optionId || usedIds.has(optionId)) {
      return
    }

    usedIds.add(optionId)
    transferredOptions.push({
      id: optionId,
      text: sourceOption?.text || fallbackLabel,
      value: sourceOption?.value ?? fallbackLabel,
      image: sourceOption?.image
    })
  })

  return transferredOptions
}

const resolveConditionQuestionId = (condition: any) =>
  condition?.questionId || condition?.sourceQuestionId || ''

const resolveQuestionReference = (
  rawQuestionRef: string,
  questions: Question[]
) => {
  const questionRef = rawQuestionRef.trim()
  if (!questionRef) return ''

  const directMatch = questions.find((question) => question.id === questionRef)
  if (directMatch) return directMatch.id

  const sequentialMatch = questionRef.match(/^#?\s*q?\s*(\d+)$/i)
  if (sequentialMatch) {
    const questionIndex = Number(sequentialMatch[1]) - 1
    return questions[questionIndex]?.id || questionRef
  }

  return questionRef
}

const evaluateConditionList = (
  conditions: any[] | undefined,
  logic: 'AND' | 'OR' | undefined,
  answers: Answers,
  questions: Question[] = []
) => {
  if (!conditions || conditions.length === 0) return true

  const results = conditions.map((condition: any) => {
    const questionId = resolveConditionQuestionId(condition)
    const conditionQuestion = questions.find((question) => question.id === questionId)
    const answer = getAnswerByPath(answers[questionId], condition.answerPath || condition.itemId || condition.rowId || condition.fieldId)
    const expectedValue = String(condition.optionId ?? condition.value ?? condition.answerValue ?? '')

    switch (condition.operator) {
      case 'is_answered':
        return isAnswerFilled(answer)
      case 'not_answered':
        return !isAnswerFilled(answer)
      case 'not_equals':
        if (expectedValue === '') return String(answer ?? '') !== ''
        return evaluateBranchConditionOnAnswer(answer, 'not_equals', expectedValue, conditionQuestion)
      case 'contains':
        return evaluateBranchConditionOnAnswer(answer, 'contains', expectedValue, conditionQuestion)
      case 'not_contains':
        return evaluateBranchConditionOnAnswer(answer, 'not_contains', expectedValue, conditionQuestion)
      case 'greater_than':
      case 'less_than':
      case 'greater_or_equal':
      case 'less_or_equal':
        return evaluateBranchConditionOnAnswer(answer, String(condition.operator), expectedValue, conditionQuestion)
      case 'equals':
        if (expectedValue === '') return String(answer ?? '') === ''
        return evaluateBranchConditionOnAnswer(answer, 'equals', expectedValue, conditionQuestion)
      default:
        return true
    }
  })

  // Conector por condicao (logicalOperator) com fallback para o E/OU global.
  let combined = !!results[0]
  for (let index = 1; index < conditions.length; index += 1) {
    const connector = String(conditions[index]?.logicalOperator || logic || 'AND').toUpperCase()
    combined = connector === 'OR' ? (combined || !!results[index]) : (combined && !!results[index])
  }
  return combined
}

export const evaluateItemDisplayConditions = (
  item: { displayConditions?: any[]; displayLogic?: 'AND' | 'OR' } | undefined,
  allQuestions: Question[],
  answers: Answers
) => evaluateConditionList(item?.displayConditions, item?.displayLogic, answers, allQuestions)

export const getVisibleQuestionOptions = (
  question: Question,
  allQuestions: Question[],
  answers: Answers
) => (question.options || []).filter((option) => evaluateItemDisplayConditions(option as any, allQuestions, answers))

export const getVisibleTextBoxes = (
  question: Question,
  allQuestions: Question[],
  answers: Answers
): NonNullable<Question['textBoxes']> => {
  const baseBoxes = (question.textBoxes || []).filter((box) => evaluateItemDisplayConditions(box as any, allQuestions, answers))
  const transferConfig = (question as any).optionTransfer
  const transferRules = Array.isArray(transferConfig?.rules) && transferConfig.rules.length > 0
    ? transferConfig.rules
    : transferConfig?.sourceQuestionId && transferConfig?.mode
      ? [transferConfig]
      : []

  if (transferRules.length === 0) {
    return baseBoxes
  }

  const mockOptions = baseBoxes.map((box) => ({ id: box.id, text: box.label, value: box.label }))
  const mockQuestion = { ...question, options: mockOptions } as Question
  const filteredOptions = getFilteredQuestionOptions(mockQuestion, allQuestions, answers)

  return filteredOptions.map((opt) => {
    const existingBox = baseBoxes.find((b) => String(b.id) === String(opt.id) || String(b.label) === String(opt.text))
    if (existingBox) return existingBox
    return {
      id: opt.id,
      label: opt.text || String(opt.id),
      type: 'text' as const,
      required: false
    }
  })
}

export const getVisibleMatrixRows = (
  question: Question,
  allQuestions: Question[],
  answers: Answers
) => (question.matrixRows || []).filter((row) => evaluateItemDisplayConditions(row as any, allQuestions, answers))

export const getVisibleMatrixColumns = (
  question: Question,
  allQuestions: Question[],
  answers: Answers
) => (question.matrixColumns || []).filter((column) => evaluateItemDisplayConditions(column as any, allQuestions, answers))

const resolveAnswerAsText = (
  questionId: string,
  questions: Question[],
  answers: Answers,
  selectionPosition?: number
) => {
  const resolvedQuestionId = resolveQuestionReference(questionId, questions)
  const sourceQuestion = questions.find((question) => question.id === resolvedQuestionId)
  const answer = answers[resolvedQuestionId]

  if (!sourceQuestion) {
    return answer === undefined || answer === null ? '' : normalizePipedAnswerText(answer)
  }

  // Classificacao legada (objeto item->nivel) ou resposta ordenada (array / orderedIds)
  let normalizedAnswers = normalizeToArray(answer)
  if (
    sourceQuestion.type === 'classification' &&
    answer &&
    typeof answer === 'object' &&
    !Array.isArray(answer) &&
    !Array.isArray((answer as any).values)
  ) {
    if (Array.isArray((answer as any).orderedIds)) {
      normalizedAnswers = (answer as any).orderedIds.map(String)
    } else {
      const optionIds = (sourceQuestion.options || []).map((option) => String(option.id))
      const orderedFromOptions = optionIds.filter((id) => isAnswerFilled((answer as any)[id]))
      const orderedFromEntries = Object.keys(answer as Record<string, unknown>).filter((key) =>
        key !== 'orderedIds' && isAnswerFilled((answer as any)[key]) && !orderedFromOptions.includes(key)
      )
      if (orderedFromOptions.length > 0 || orderedFromEntries.length > 0) {
        normalizedAnswers = [...orderedFromOptions, ...orderedFromEntries]
      }
    }
  }

  const answersToRender = typeof selectionPosition === 'number'
    ? normalizedAnswers.slice(selectionPosition, selectionPosition + 1)
    : normalizedAnswers

  if (answersToRender.length > 0) {
    const visibleOptions = getFilteredQuestionOptions(sourceQuestion, questions, answers)
    const labels = answersToRender.map((value) => {
      const isOther = typeof value === 'string' && (value === OTHER_OPTION_ID || value.trim().toLowerCase() === '_other_' || value.trim().toLowerCase() === 'other')
      if (isOther) {
        const otherLabel = sourceQuestion.otherLabel || 'Outro'
        const otherText = getOtherAnswerText(answer)
        return normalizePipedAnswerText(otherText || otherLabel)
      }

      const option = findOptionByStoredValue(sourceQuestion.options, String(value)) ||
        visibleOptions.find((item) => (
          String(item.id) === String(value) ||
          String(item.value ?? '') === String(value)
        ))
      return normalizePipedAnswerText(option?.text || String(value))
    })

    return labels.filter(Boolean).join(', ')
  }

  return answer === undefined || answer === null ? '' : normalizePipedAnswerText(answer)
}

export const isAnswerFilled = (answer: any): boolean => {
  if (Array.isArray(answer)) return answer.length > 0
  if (answer && typeof answer === 'object') {
    if (Array.isArray(answer.values)) return answer.values.length > 0
    if (answer.value !== undefined && answer.value !== null && answer.value !== '') return true
    return Object.values(answer).some((value) => isAnswerFilled(value))
  }
  return !(answer === undefined || answer === null || answer === '')
}

const BRANCH_NUMERIC_OPERATORS = ['greater_than', 'less_than', 'greater_or_equal', 'less_or_equal']

const resolveBranchExpectedValue = (condition: any) => String(
  condition?.value ?? condition?.answerValue ?? condition?.optionId ?? ''
)

const parseBranchNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null
  const parsed = Number(typeof value === 'string' ? value.replace(',', '.') : value)
  return Number.isFinite(parsed) ? parsed : null
}

const normalizeBranchComparableText = (value: unknown) => normalizeComparableText(stripHtml(String(value ?? '')))

// Valores guardados pelo app que devem ser aceitos como resposta esperada do desvio.
// Compara id, value e texto da opcao/coluna da pergunta de origem para cobrir
// desvios configurados com texto em vez do id da opcao (e vice-versa).
const buildAcceptedStoredValues = (
  expectedValue: string,
  sourceQuestion: Question | undefined
): Set<string> => {
  const accepted = new Set<string>()
  const addCandidate = (value: unknown) => {
    const raw = String(value ?? '')
    if (raw === '') return
    accepted.add(raw)
    const normalized = normalizeBranchComparableText(raw)
    if (normalized !== '') accepted.add(normalized)
  }

  addCandidate(expectedValue)

  if (!sourceQuestion) return accepted

  const expectedRaw = String(expectedValue ?? '')
  const expectedNormalized = normalizeBranchComparableText(expectedValue)

  const collectMatches = (id: unknown, value: unknown, text: unknown) => {
    const identifiers = [String(id ?? ''), String(value ?? '')].filter((item) => item !== '')
    const labels = [String(text ?? '')].filter((item) => item !== '')

    const referencesExpected = identifiers.includes(expectedRaw) ||
      labels.some((label) => normalizeBranchComparableText(label) === expectedNormalized)

    if (!referencesExpected) return

    identifiers.forEach(addCandidate)
    labels.forEach(addCandidate)
  }

  ;(sourceQuestion.options || []).forEach((option) => collectMatches(option.id, option.value, option.text))
  ;(sourceQuestion.matrixColumns || []).forEach((column) => collectMatches(column.id, column.value ?? column.id, column.label))

  return accepted
}

const storedValueMatchesExpected = (
  storedValue: unknown,
  expectedValue: string,
  sourceQuestion: Question | undefined
): boolean => {
  const raw = String(storedValue ?? '')
  if (raw === '') return false

  const accepted = buildAcceptedStoredValues(expectedValue, sourceQuestion)
  return accepted.has(raw) || accepted.has(normalizeBranchComparableText(raw))
}

const compareBranchNumeric = (
  storedValue: unknown,
  expectedValue: string,
  operator: string
): boolean => {
  const storedNumber = parseBranchNumber(storedValue)
  const expectedNumber = parseBranchNumber(expectedValue)
  if (storedNumber === null || expectedNumber === null) return false

  switch (operator) {
    case 'greater_than': return storedNumber > expectedNumber
    case 'less_than': return storedNumber < expectedNumber
    case 'greater_or_equal': return storedNumber >= expectedNumber
    case 'less_or_equal': return storedNumber <= expectedNumber
    default: return false
  }
}

// Respostas compostas (matriz, melhor/pior, campos multi-texto) sem answerPath
// viram lista de celulas: "algum item = Sim" passa a funcionar no desvio.
const getBranchAnswerCandidates = (answer: unknown): unknown[] => {
  if (answer === undefined || answer === null) return []
  if (Array.isArray(answer)) return answer
  if (typeof answer === 'object') {
    const record = answer as Record<string, unknown>
    if (Array.isArray(record.values)) return record.values
    if (record.value !== undefined) return [record.value]
    return Object.values(record)
  }
  return [answer]
}

const evaluateBranchConditionOnAnswer = (
  answer: unknown,
  operator: string,
  expectedValue: string,
  sourceQuestion: Question | undefined
): boolean => {
  const filledCandidates = getBranchAnswerCandidates(answer).filter((candidate) => (
    candidate !== undefined &&
    candidate !== null &&
    candidate !== '' &&
    !(typeof candidate === 'number' && Number.isNaN(candidate))
  ))

  if (operator === 'is_answered') return filledCandidates.length > 0
  if (operator === 'not_answered') return filledCandidates.length === 0

  if (BRANCH_NUMERIC_OPERATORS.includes(operator)) {
    return filledCandidates.some((candidate) => compareBranchNumeric(candidate, expectedValue, operator))
  }

  switch (operator) {
    case 'not_equals':
      return filledCandidates.length > 0 &&
        filledCandidates.every((candidate) => !storedValueMatchesExpected(candidate, expectedValue, sourceQuestion))
    case 'not_contains':
      return filledCandidates.length > 0 &&
        filledCandidates.every((candidate) => (
          !storedValueMatchesExpected(candidate, expectedValue, sourceQuestion) &&
          !String(candidate ?? '').includes(expectedValue)
        ))
    case 'contains':
      return filledCandidates.some((candidate) => (
        storedValueMatchesExpected(candidate, expectedValue, sourceQuestion) ||
        String(candidate ?? '').includes(expectedValue)
      ))
    case 'equals':
    default:
      return filledCandidates.some((candidate) => storedValueMatchesExpected(candidate, expectedValue, sourceQuestion))
  }
}

export const isQuestionAnswerComplete = (
  question: Question,
  answer: any,
  allQuestions: Question[] = [],
  allAnswers: Answers = {}
): boolean => {
  if (question.type === 'multitext' && getVisibleTextBoxes(question, allQuestions, allAnswers).length === 0) {
    return true
  }

  if (['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type) &&
    getVisibleMatrixRows(question, allQuestions, allAnswers).length === 0) {
    return true
  }

  if (['matrix', 'dropdown_matrix'].includes(question.type) &&
    getVisibleMatrixColumns(question, allQuestions, allAnswers).length === 0) {
    return true
  }

  if (['multiple', 'single', 'checkbox', 'dropdown', 'ranking', 'bestworst'].includes(question.type) &&
    getFilteredQuestionOptions(question, allQuestions, allAnswers).length === 0 &&
    !question.allowOther) {
    return true
  }

  if (question.type === 'classification' &&
    getFilteredQuestionOptions(question, allQuestions, allAnswers).length === 0) {
    return true
  }

  if (!isAnswerFilled(answer)) return false

  const values = normalizeToArray(answer)
  const visibleOptions = getFilteredQuestionOptions(question, allQuestions, allAnswers)

  if (['multiple', 'single', 'checkbox', 'dropdown', 'ranking', 'classification'].includes(question.type)) {
    if (Array.isArray(answer) || (answer && typeof answer === 'object' && Array.isArray((answer as any).values))) {
      if (!values.every((value) => isVisibleChoiceValue(value, visibleOptions, question.allowOther))) {
        return false
      }
    }
  }

  if (question.allowOther && question.requireOtherText !== false && values.includes(OTHER_OPTION_ID)) {
    if (!getOtherAnswerText(answer)) return false
  }

  if (question.type === 'multitext') {
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
    const boxesToValidate = getVisibleTextBoxes(question, allQuestions, allAnswers)
    const missingRequiredBox = boxesToValidate.some((box) => (
      box.required && !isAnswerFilled(currentValues[box.id])
    ))
    if (missingRequiredBox) return false

    if (question.sumValidation?.enabled) {
      const numericTotal = boxesToValidate.reduce((total, box) => {
        if (box.type !== 'number') return total
        const value = Number(currentValues[box.id] || 0)
        return total + (Number.isFinite(value) ? value : 0)
      }, 0)
      if (numericTotal !== Number(question.sumValidation.targetSum)) return false
    }
  }

  if (['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type) && question.required) {
    const currentValues = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}
    const rowsToValidate = getVisibleMatrixRows(question, allQuestions, allAnswers)
    if (['matrix', 'dropdown_matrix'].includes(question.type)) {
      const visibleColumns = getVisibleMatrixColumns(question, allQuestions, allAnswers)
      return rowsToValidate.every((row) => {
        const rowValue = currentValues[row.id]
        return isAnswerFilled(rowValue) &&
          visibleColumns.some((column) => matrixColumnMatchesStoredValue(column, String(rowValue)))
      })
    }

    return rowsToValidate.every((row) => isAnswerFilled(currentValues[row.id]))
  }

  if (question.type === 'classification' && question.required) {
    // Resposta ordenada: array na ordem de clique (compatível com legado item->nivel)
    if (Array.isArray(answer)) {
      return answer.length >= (question.validation?.minSelections || 1) &&
        (!question.validation?.maxSelections || answer.length <= question.validation.maxSelections) &&
        answer.every((value) => isVisibleChoiceValue(String(value), visibleOptions, question.allowOther))
    }
    if (answer && typeof answer === 'object' && Array.isArray((answer as any).values)) {
      const orderedValues = (answer as any).values.map(String)
      return orderedValues.length >= (question.validation?.minSelections || 1) &&
        (!question.validation?.maxSelections || orderedValues.length <= question.validation.maxSelections) &&
        orderedValues.every((value: string) => isVisibleChoiceValue(value, visibleOptions, question.allowOther))
    }
    if (answer && typeof answer === 'object' && Array.isArray((answer as any).orderedIds)) {
      const orderedValues = (answer as any).orderedIds.map(String)
      return orderedValues.length > 0 && orderedValues.every((value: string) => isVisibleChoiceValue(value, visibleOptions, false))
    }
    if (answer && typeof answer === 'object') {
      const filled = Object.keys(answer).filter((key) => key !== 'orderedIds' && isAnswerFilled((answer as any)[key]))
      return filled.length > 0
    }
    return false
  }

  if (question.type === 'bestworst' && question.required) {
    return !!answer?.best &&
      !!answer?.worst &&
      answer.best !== answer.worst &&
      isVisibleChoiceValue(String(answer.best), visibleOptions, false) &&
      isVisibleChoiceValue(String(answer.worst), visibleOptions, false)
  }

  return true
}

export const evaluateDisplayConditions = (
  question: Question,
  allQuestions: Question[],
  answers: Answers
): boolean => {
  return evaluateConditionList(question.displayConditions, question.displayLogic, answers, allQuestions)
}

export const evaluatePageDisplayConditions = (
  page: SurveyPage | undefined,
  allQuestions: Question[],
  answers: Answers
) => evaluateConditionList((page as any)?.displayConditions, (page as any)?.displayLogic, answers, allQuestions)

export const resolveDynamicText = (
  template: string | undefined,
  questions: Question[],
  answers: Answers
) => {
  if (!template) return ''

  return template
    .replace(/\{\{\s*(?:resposta|answer)\s*:\s*([^}]+)\}\}/gi, (_, rawQuestionId: string) => {
      const [rawId, rawPosition] = rawQuestionId.split(':')
      const position = rawPosition ? Math.max(0, Number(rawPosition.trim()) - 1) : undefined
      return resolveAnswerAsText(rawId, questions, answers, Number.isFinite(position) ? position : undefined)
    })
    .replace(/\[\s*(#?\s*q?\s*\d+)(?:\s*:\s*(\d+))?\s*\]/gi, (_, rawQuestionRef: string, rawPosition: string) => {
      const position = rawPosition ? Math.max(0, Number(rawPosition) - 1) : undefined
      return resolveAnswerAsText(rawQuestionRef, questions, answers, Number.isFinite(position) ? position : undefined)
    })
}

export const getResolvedQuestionContent = (
  question: Question,
  questions: Question[],
  answers: Answers
) => {
  const dynamicConfig = (question as any).answerPiping || (question as any).textTransfer
  const titleTemplate = dynamicConfig?.titleTemplate || question.title
  const descriptionTemplate = dynamicConfig?.descriptionTemplate || question.description

  return {
    title: resolveDynamicText(titleTemplate, questions, answers),
    description: resolveDynamicText(descriptionTemplate, questions, answers)
  }
}

export function getFilteredQuestionOptions(
  question: Question,
  questions: Question[],
  answers: Answers
) {
  const baseOptions = getVisibleQuestionOptions(question, questions, answers)
  const transferConfig = (question as any).optionTransfer
  const transferRules = Array.isArray(transferConfig?.rules) && transferConfig.rules.length > 0
    ? transferConfig.rules
    : transferConfig?.sourceQuestionId && transferConfig?.mode
      ? [transferConfig]
      : []

  if (transferRules.length === 0) {
    return baseOptions
  }

  const firstSourceQuestionId = resolveQuestionReference(transferRules[0]?.sourceQuestionId || '', questions)
  const firstSourceQuestion = questions.find((item) => item.id === firstSourceQuestionId)
  const runtimeOptions = baseOptions.length > 0
    ? baseOptions
    : (firstSourceQuestion?.options || []).map((option) => ({ ...option }))

  const applyTransferRule = (
    currentOptions: Question['options'],
    rule: any
  ) => {
    const sourceQuestionId = resolveQuestionReference(rule.sourceQuestionId || '', questions)
    const sourceQuestion = questions.find((item) => item.id === sourceQuestionId)
    const sourceAnswer = answers[sourceQuestionId]
    const selectedAnswers = normalizeToArray(sourceAnswer)
    const comparableValues = buildTransferComparableValues(selectedAnswers, sourceQuestion, sourceAnswer)

    if (comparableValues.size === 0) {
      return currentOptions
    }

    if (rule.mode === 'include_selected') {
      const includedOptions = (currentOptions || []).filter((option) => optionMatchesComparableValues(option, comparableValues))
      const missingSelectedAnswers = selectedAnswers.filter((selectedAnswer) => {
        const singleComparableValues = buildTransferComparableValues([selectedAnswer], sourceQuestion, sourceAnswer)
        return !(currentOptions || []).some((option) => optionMatchesComparableValues(option, singleComparableValues))
      })

      const generatedOptions = buildTransferredOptionsFromAnswers(missingSelectedAnswers, sourceQuestion, sourceAnswer)
      const optionMap = new Map<string, NonNullable<Question['options']>[number]>()

      ;[...includedOptions, ...(generatedOptions || [])].forEach((option) => {
        optionMap.set(String(option.id), option)
      })

      return Array.from(optionMap.values())
    }

    if (rule.mode === 'exclude_selected') {
      return (currentOptions || []).filter((option) => !optionMatchesComparableValues(option, comparableValues))
    }

    return currentOptions
  }

  const logic = transferConfig?.logic || 'AND'

  if (logic === 'OR' && transferRules.some((rule: any) => rule.mode === 'include_selected')) {
    const optionMap = new Map<string, NonNullable<Question['options']>[number]>()
    transferRules.forEach((rule: any) => {
      applyTransferRule(runtimeOptions, rule)?.forEach((option) => {
        optionMap.set(String(option.id), option)
      })
    })
    return Array.from(optionMap.values()).filter((option) => evaluateItemDisplayConditions(option as any, questions, answers))
  }

  const transferredOptions = transferRules.reduce(
    (currentOptions: Question['options'], rule: any) => applyTransferRule(currentOptions, rule),
    runtimeOptions
  )

  return (transferredOptions || []).filter((option) => evaluateItemDisplayConditions(option as any, questions, answers))
}

const getNextTerminalPage = (
  currentQuestion: Question,
  pages: SurveyPage[],
  orderedQuestions: Question[],
  answers: Answers
) => {
  const currentPageIndex = pages.findIndex((page) => (page.questionIds || []).includes(currentQuestion.id))
  if (currentPageIndex < 0) return null

  return pages.slice(currentPageIndex + 1).find((page) => (
    (!page.questionIds || page.questionIds.length === 0) &&
    evaluatePageDisplayConditions(page, orderedQuestions, answers)
  )) || null
}

const evaluateBranchConditions = (
  branch: any,
  fallbackQuestionId: string,
  answers: Answers,
  questions: Question[] = []
) => {
  if (!Array.isArray(branch.conditions) || branch.conditions.length === 0) return null

  const branchResults = branch.conditions.map((condition: any) => {
    const conditionQuestionId = resolveConditionQuestionId(condition) || fallbackQuestionId
    const conditionQuestion = questions.find((question) => question.id === conditionQuestionId)
    const currentAnswer = getAnswerByPath(
      answers[conditionQuestionId],
      condition.answerPath || condition.itemId || condition.rowId || condition.fieldId
    )

    return evaluateBranchConditionOnAnswer(
      currentAnswer,
      String(condition.operator || 'equals'),
      resolveBranchExpectedValue(condition),
      conditionQuestion
    )
  })

  return (branch.logic || 'AND') === 'OR'
    ? branchResults.some(Boolean)
    : branchResults.every(Boolean)
}

const resolveTargetPageLocation = (
  targetPageId: string,
  orderedQuestions: Question[],
  indexMap: Map<string, number>,
  answers: Answers,
  pages: SurveyPage[]
): SurveyLocation | null => {
  const targetPage = pages.find((page) => page.id === targetPageId)
  if (!targetPage) return null
  if (!evaluatePageDisplayConditions(targetPage, orderedQuestions, answers)) return null

  if (!targetPage.questionIds || targetPage.questionIds.length === 0) {
    return { kind: 'terminal', page: targetPage }
  }

  for (const questionId of targetPage.questionIds) {
    const questionIndex = indexMap.get(questionId)
    if (questionIndex === undefined) continue

    const targetQuestion = orderedQuestions[questionIndex]
    if (targetQuestion && isQuestionVisibleInSurvey(targetQuestion, orderedQuestions, answers, pages)) {
      return { kind: 'question', index: questionIndex }
    }
  }

  const fallbackQuestionIndex = indexMap.get(targetPage.questionIds[0])
  if (fallbackQuestionIndex !== undefined) {
    return { kind: 'question', index: fallbackQuestionIndex }
  }

  return { kind: 'terminal', page: targetPage }
}

// Localiza a pergunta de destino de um desvio: se a pergunta alvo estiver oculta
// (condicao de exibicao), segue para a proxima visivel depois dela.
const resolveTargetQuestionLocation = (
  targetQuestionId: string,
  orderedQuestions: Question[],
  indexMap: Map<string, number>,
  answers: Answers,
  pages: SurveyPage[]
): SurveyLocation | null => {
  const questionIndex = indexMap.get(targetQuestionId)
  if (questionIndex === undefined) return null

  const targetQuestion = orderedQuestions[questionIndex]
  if (targetQuestion && isQuestionVisibleInSurvey(targetQuestion, orderedQuestions, answers, pages)) {
    return { kind: 'question', index: questionIndex }
  }

  for (let index = questionIndex + 1; index < orderedQuestions.length; index += 1) {
    if (isQuestionVisibleInSurvey(orderedQuestions[index], orderedQuestions, answers, pages)) {
      return { kind: 'question', index }
    }
  }

  return null
}

const findCurrentPage = (
  currentQuestion: Question,
  pages: SurveyPage[]
) => pages.find((page) => (page.questionIds || []).includes(currentQuestion.id)) || null

const isQuestionVisibleInSurvey = (
  question: Question,
  orderedQuestions: Question[],
  answers: Answers,
  pages: SurveyPage[]
) => {
  const page = findCurrentPage(question, pages)
  return evaluateDisplayConditions(question, orderedQuestions, answers) &&
    evaluatePageDisplayConditions(page, orderedQuestions, answers)
}

const hasNextVisibleQuestionInSamePage = (
  currentIndex: number,
  currentPage: SurveyPage,
  orderedQuestions: Question[],
  indexMap: Map<string, number>,
  answers: Answers
) => {
  const pageQuestionIndexes = (currentPage.questionIds || [])
    .map((questionId) => indexMap.get(questionId))
    .filter((index): index is number => typeof index === 'number')
    .sort((left, right) => left - right)

  return pageQuestionIndexes.some((questionIndex) => (
    questionIndex > currentIndex &&
    isQuestionVisibleInSurvey(orderedQuestions[questionIndex], orderedQuestions, answers, [currentPage])
  ))
}

export const resolvePageBranchTarget = (
  page: SurveyPage,
  orderedQuestions: Question[],
  indexMap: Map<string, number>,
  answers: Answers,
  pages: SurveyPage[],
  isPageEnd: boolean = true
): SurveyLocation | null => {
  const branches: any[] = (page as any).conditionalBranches || []
  if (branches.length === 0) return null

  for (const branch of branches) {
    const hasConditions = Array.isArray(branch.conditions) && branch.conditions.length > 0

    // Desvio sem condicao ("aplicar sempre ao concluir esta pagina") so dispara no fim.
    // Desvio com condicao dispara assim que a resposta que o satisfaz for dada; para nao
    // disparar com condicoes AND ainda parcialmente respondidas (ou "estiver vazia"),
    // exige que todas as perguntas das condicoes tenham resposta antes do fim da pagina.
    if (!isPageEnd) {
      if (!hasConditions) continue
      const allConditionAnswered = branch.conditions.every((condition: any) => {
        const conditionQuestionId = resolveConditionQuestionId(condition)
        return !!conditionQuestionId && isAnswerFilled(answers[conditionQuestionId])
      })
      if (!allConditionAnswered) continue
    }

    const matches = hasConditions ? evaluateBranchConditions(branch, '', answers, orderedQuestions) : true
    if (!matches) continue

    if (branch.targetType === 'complete') return { kind: 'terminal', page: buildOutcomeTerminal('completed') }
    if (branch.targetType === 'cancel_filter') return { kind: 'terminal', page: buildOutcomeTerminal('cancel_filter') }

    if (branch.targetType === 'question' && branch.targetQuestionId) {
      const location = resolveTargetQuestionLocation(branch.targetQuestionId, orderedQuestions, indexMap, answers, pages)
      if (location) return location
      continue
    }

    if (!branch.targetPageId || branch.targetPageId === page.id) continue

    const location = resolveTargetPageLocation(branch.targetPageId, orderedQuestions, indexMap, answers, pages)
    if (location) return location
  }

  return null
}

export const getUnansweredRequiredQuestions = (
  questions: Question[],
  answers: Answers
) => questions.filter((question) => (
  question.required &&
  evaluateDisplayConditions(question, questions, answers) &&
  !isQuestionAnswerComplete(question, answers[question.id], questions, answers)
))

export const getPreviousVisibleQuestionIndex = (
  currentIndex: number,
  questions: Question[],
  answers: Answers,
  pages: SurveyPage[] = []
) => {
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    if (isQuestionVisibleInSurvey(questions[index], questions, answers, pages)) {
      return index
    }
  }

  return null
}

export const resolveBranchTarget = (
  question: Question,
  orderedQuestions: Question[],
  indexMap: Map<string, number>,
  answers: Answers,
  pages: SurveyPage[]
): SurveyLocation | null => {
  const branches: any[] = (question as any).conditionalBranches || []
  if (branches.length === 0) return null

  for (const branch of branches) {
    const answer = getAnswerByPath(
      answers[question.id],
      branch.answerPath || branch.itemId || branch.rowId || branch.fieldId
    )
    let matches = false

    if (Array.isArray(branch.conditions) && branch.conditions.length > 0) {
      matches = !!evaluateBranchConditions(branch, question.id, answers, orderedQuestions)
    } else {
      // Formato legado (optionId + condition): aceita tambem operadores
      // numericos (maior/menor) e compara por id, value ou texto da opcao.
      const operator = String(branch.condition || branch.operator || 'equals')
      const expectedValue = String(branch.optionId ?? branch.value ?? branch.answerValue ?? '')
      matches = evaluateBranchConditionOnAnswer(answer, operator, expectedValue, question)
    }

    if (!matches) continue

    if (branch.targetType === 'complete') return { kind: 'terminal', page: buildOutcomeTerminal('completed') }
    if (branch.targetType === 'cancel_filter') return { kind: 'terminal', page: buildOutcomeTerminal('cancel_filter') }

    if ((branch.targetType === 'question' || !branch.targetType) && branch.targetQuestionId) {
      const questionIndex = indexMap.get(branch.targetQuestionId)
      if (questionIndex !== undefined) {
        return { kind: 'question', index: questionIndex }
      }
    }

    if (branch.targetPageId) {
      const location = resolveTargetPageLocation(branch.targetPageId, orderedQuestions, indexMap, answers, pages)
      if (location) return location
    }
  }

  return null
}

export const getNextSurveyLocation = (
  currentIndex: number,
  orderedQuestions: Question[],
  indexMap: Map<string, number>,
  answers: Answers,
  pages: SurveyPage[]
): SurveyLocation | null => {
  if (orderedQuestions.length === 0) return null

  const currentQuestion = orderedQuestions[currentIndex]
  if (currentQuestion) {
    const branchTarget = resolveBranchTarget(currentQuestion, orderedQuestions, indexMap, answers, pages)
    if (branchTarget) {
      if (branchTarget.kind === 'terminal') {
        return branchTarget
      }

      const targetQuestion = orderedQuestions[branchTarget.index]
      if (targetQuestion && isQuestionVisibleInSurvey(targetQuestion, orderedQuestions, answers, pages)) {
        return branchTarget
      }

      for (let index = branchTarget.index + 1; index < orderedQuestions.length; index += 1) {
        if (isQuestionVisibleInSurvey(orderedQuestions[index], orderedQuestions, answers, pages)) {
          return { kind: 'question', index }
        }
      }

      // Alvo do desvio inalcancavel: segue o fluxo normal (desvio de pagina e
      // sequencia linear) em vez de encerrar a entrevista neste ponto.
    }

    const currentPage = findCurrentPage(currentQuestion, pages)
    if (currentPage) {
      const isPageEnd = !hasNextVisibleQuestionInSamePage(currentIndex, currentPage, orderedQuestions, indexMap, answers)
      const pageBranchTarget = resolvePageBranchTarget(currentPage, orderedQuestions, indexMap, answers, pages, isPageEnd)
      if (pageBranchTarget) {
        return pageBranchTarget
      }
    }
  }

  for (let index = currentIndex + 1; index < orderedQuestions.length; index += 1) {
    if (isQuestionVisibleInSurvey(orderedQuestions[index], orderedQuestions, answers, pages)) {
      return { kind: 'question', index }
    }
  }

  if (currentQuestion) {
    const terminalPage = getNextTerminalPage(currentQuestion, pages, orderedQuestions, answers)
    if (terminalPage) {
      return { kind: 'terminal', page: terminalPage }
    }
  }

  return null
}
